import { gstFromInclusive, isInterStateSupply } from "./gst";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Gross bill amount before bill-level discount (matches POS "Total amount"). */
export function saleGrossAmount(sale) {
  return round2((Number(sale?.total) || 0) + (Number(sale?.discount) || 0));
}

export function saleBillAmounts(sale) {
  return {
    amount: saleGrossAmount(sale),
    discount: round2(sale?.discount || 0),
    beforeTax: round2(sale?.subtotal || 0),
    tax: round2(sale?.tax_amount || 0),
    afterTax: round2(sale?.total || 0),
  };
}

export function paymentStatusLabel(status) {
  if (status === "paid") return "Paid";
  if (status === "partial") return "Partial";
  return "Pending";
}

export function paymentStatusStyle(status) {
  if (status === "paid") {
    return { background: "var(--surface-success)", color: "var(--success)", dot: "var(--success)" };
  }
  if (status === "partial") {
    return { background: "#FFF8E1", color: "#C17900", dot: "#E65100" };
  }
  return { background: "var(--surface-error)", color: "var(--error)", dot: "var(--error)" };
}

export function gstSummaryLines(sale) {
  if ((sale.igst_amount || 0) > 0) {
    return [{ label: "IGST", amount: sale.igst_amount }];
  }
  if ((sale.cgst_amount || 0) > 0 || (sale.sgst_amount || 0) > 0) {
    return [
      { label: "CGST", amount: sale.cgst_amount || 0 },
      { label: "SGST", amount: sale.sgst_amount || 0 },
    ];
  }
  if ((sale.tax_amount || 0) > 0) {
    return [{ label: `GST (${sale.tax_percent}%)`, amount: sale.tax_amount }];
  }
  return [];
}

export function invoiceLineItems(sale, priceIncludesGst) {
  const storeState = sale.store_state || "";
  const customerState = sale.customer_snapshot?.state || "";
  const interState = isInterStateSupply(storeState, customerState);

  return (sale.items || []).map((it, index) => {
    const lineTotal = Number(it.line_total) || 0;
    const rate = Number(it.gst_percent ?? 5) || 0;
    const qty = Number(it.quantity) || 0;
    const taxable = priceIncludesGst ? round2(lineTotal - gstFromInclusive(lineTotal, rate)) : round2(lineTotal);
    const gstAmt = priceIncludesGst ? round2(gstFromInclusive(lineTotal, rate)) : round2(lineTotal * (rate / 100));
    const cgst = interState ? 0 : round2(gstAmt / 2);
    const sgst = interState ? 0 : round2(gstAmt - cgst);
    const igst = interState ? gstAmt : 0;

    const unitPriceBeforeTax = qty > 0 ? round2(taxable / qty) : 0;

    return {
      sr: index + 1,
      name: it.product_name,
      barcode: it.barcode,
      hsn_code: it.hsn_code || "",
      qty,
      unitPriceBeforeTax,
      rate,
      taxable,
      cgst,
      sgst,
      igst,
      lineTotal,
    };
  });
}

export function storeBillingAddress(store) {
  if (!store) return null;
  const same = store.billing_same_as_office !== false;
  if (same) {
    return {
      address: store.address,
      city: store.city,
      state: store.state,
      country: store.country || "India",
      pincode: store.pincode,
    };
  }
  return {
    address: store.billing_address,
    city: store.billing_city,
    state: store.billing_state,
    country: store.billing_country || "India",
    pincode: store.billing_pincode,
  };
}

export function formatInvoiceDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatSaleDate(iso) {
  return formatInvoiceDate(iso);
}

export function formatReceiptDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

export function publicInvoiceUrl(publicToken) {
  if (!publicToken) return "";
  const path = `/invoice/${publicToken}`;
  const siteUrl = (process.env.REACT_APP_PUBLIC_SITE_URL || "").replace(/\/$/, "");
  if (siteUrl) {
    return `${siteUrl}${path}`;
  }
  if (typeof window !== "undefined") {
    return `${normalizePublicOrigin(window.location)}${path}`;
  }
  return path;
}

function normalizePublicOrigin(location) {
  const { hostname, port } = location;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `http://127.0.0.1:${port || "3000"}`;
  }
  return location.origin;
}

export function whatsAppShareUrl(phone, message) {
  const text = encodeURIComponent(message);
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return `https://wa.me/?text=${text}`;
  const normalized = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${normalized}?text=${text}`;
}
